import React, { useState, useEffect } from "react";
import { ProfileDropdown } from "./ProfileDropdown";
import { BranchSelector } from "./BranchSelector";
import { NotificationDropdown } from "./NotificationDropdown";
import { WhatsNewDropdown } from "./WhatsNewDropdown";
import { Chatbot } from "../../Chatbot";
import { useDarkMode } from "../../../contexts/DarkModeContext";
import g360LogoLight from "../../../images/g360_logo_light.svg";
import g360LogoDark from "../../../images/g360_logo_dark.svg";
import type { UserInfo, Branch } from "../../../types/user";

interface HeaderProps {
  title?: string;
  subtitle?: string;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
  onLogout?: () => void;
  userInfo?: UserInfo;
  currentBranch?: Branch | null;
  branches?: Branch[];
  onBranchChange?: (branch: Branch) => void;
  showChatbot?: boolean;
  onChatbotNavigate?: (view: string) => void;
  onNotificationNavigate?: (screenId: string) => void;
  onWhatsNewNavigate?: (screenId: string) => void;
  showNewUpdatesNotification?: boolean;
  showSystemNotifications?: boolean;
  onLogoClick?: () => void;
  /** When false, branch dropdown is hidden even if user has branch_switching permission */
  showBranchSelector?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  title = "Global Components",
  subtitle = "Configuration-Driven UI System",
  showMenuButton = false,
  onMenuClick,
  onLogout,
  userInfo,
  currentBranch,
  branches = [],
  onBranchChange,
  showChatbot = false,
  onChatbotNavigate,
  onNotificationNavigate,
  onWhatsNewNavigate,
  showNewUpdatesNotification = true,
  showSystemNotifications = true,
  onLogoClick,
  showBranchSelector = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = React.useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      setIsMobile(mobile);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white dark:!bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm z-[1000] flex items-center justify-between px-4 font-sans transition-colors duration-200">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        {/* Menu Button */}
        <button
          className={`bg-none border-none text-lg cursor-pointer p-2 rounded-lg text-gray-700 dark:text-gray-200 flex items-center justify-center w-10 h-10 transition-colors duration-200 ${showMenuButton ? "flex" : "hidden"
            } ${isMenuHovered ? "bg-gray-100 dark:bg-gray-700" : ""}`}
          onClick={onMenuClick}
          onMouseEnter={() => setIsMenuHovered(true)}
          onMouseLeave={() => setIsMenuHovered(false)}
          aria-label="Toggle menu"
        >
          ☰
        </button>

        {/* Logo */}
        <div
          className="flex flex-col items-start cursor-pointer hover:opacity-80 transition-opacity"
          onClick={onLogoClick}
        >
          <img
            src={isDarkMode ? g360LogoDark : g360LogoLight}
            alt="G360"
            className={`h-8 w-auto ${isMobile ? "hidden" : "block"}`}
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Chatbot - Only show on inner pages (not home/dashboard) */}
        {showChatbot && <Chatbot position="header" onNavigate={onChatbotNavigate} />}

        {/* What's New - Only show if enabled in settings */}
        {showNewUpdatesNotification && (
          <WhatsNewDropdown onNavigate={onWhatsNewNavigate} />
        )}

        {/* Notifications - Only show if enabled in settings */}
        {showSystemNotifications && (
          <NotificationDropdown onNavigate={onNotificationNavigate} />
        )}

        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none"
          aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          data-tour="dark-mode"
        >
          {isDarkMode ? (
            // Sun icon for dark mode (clicking switches to light)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            // Moon icon for light mode (clicking switches to dark)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {/* Branch Selector - user permission + Settings session toggle */}
        {showBranchSelector &&
          currentBranch &&
          branches &&
          branches.length > 0 &&
          onBranchChange && (
            <div data-tour="branch-selector">
              <BranchSelector
                currentBranch={currentBranch}
                branches={branches}
                onBranchChange={onBranchChange}
              />
            </div>
          )}

        {/* Profile Dropdown */}
        {userInfo && onLogout ? (
          <ProfileDropdown userInfo={userInfo} onLogout={onLogout} />
        ) : (
          <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
            <div className="w-[26px] h-[26px] rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-semibold">
              U
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 m-0">User</span>
          </div>
        )}
      </div>
    </header>
  );
};
